import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Loading, Empty, SectionHeader, Card, fmtDateTime, downloadCsv, ExportButton } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const ACTION_LABEL = {
  gsa_toggled: "Activation GSA",
  subscription_updated: "Abonnement modifié",
  support_updated: "Statut support modifié",
  referral_status_updated: "Parrainage modifié",
  referral_config_updated: "Config récompense modifiée",
  pricing_plan_created: "Plan tarifaire créé",
  pricing_plan_updated: "Plan tarifaire modifié",
  pricing_plan_deleted: "Plan tarifaire supprimé",
};

const ACTION_COLORS = {
  gsa_toggled: "bg-sky-100 text-sky-700",
  subscription_updated: "bg-emerald-100 text-emerald-700",
  support_updated: "bg-amber-100 text-amber-700",
  referral_status_updated: "bg-violet-100 text-violet-700",
  referral_config_updated: "bg-violet-100 text-violet-700",
  pricing_plan_created: "bg-teal-100 text-teal-700",
  pricing_plan_updated: "bg-teal-100 text-teal-700",
  pricing_plan_deleted: "bg-red-100 text-red-700",
};

const detailStr = (d) => Object.entries(d || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");

export default function AdminAudit() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data.logs || [])).catch(() => setLogs([])); }, []);
  if (logs === null) return <Loading />;

  const exportCsv = () => downloadCsv(
    "audit-log.csv",
    ["Date", "Administrateur", "Action", "Type cible", "Cible", "Détails"],
    logs.map((l) => [l.created_at || "", l.admin_email || "", l.action || "", l.target_type || "",
      l.target_name || "", detailStr(l.details)]),
  );

  return (
    <div data-testid="admin-audit">
      <div className="mb-5 flex items-start justify-between gap-4">
        <SectionHeader title="Audit Log" subtitle="Journal des 200 dernières actions administratives." />
        <ExportButton onClick={exportCsv} testid="export-audit" />
      </div>
      {logs.length === 0 ? <Empty label="Aucune action journalisée." testid="audit-empty" /> : (
        <Card>
          <SelectableTable rows={logs} rowId={(l) => l.id} filename="audit.csv" ariaLabel="Journal d'audit — sélection et défilement" csvColumns={[{ label: "Date", get: (l) => l.created_at }, { label: "Administrateur", get: (l) => l.admin_email }, { label: "Action", get: (l) => l.action }, { label: "Cible", get: (l) => l.target_name }, { label: "Détails", get: (l) => detailStr(l.details) }]}>
            <table className="w-full min-w-[820px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3"><SelectAllHeaderCheckbox /></th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Administrateur</th><th className="px-5 py-3">Action</th>
                <th className="px-5 py-3">Cible</th><th className="px-5 py-3">Détails</th>
              </tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3"><RowCheckbox id={l.id} /></td>
                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{l.admin_email}</td>
                    <td className="px-5 py-3"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-600"}`}>{ACTION_LABEL[l.action] || l.action}</span></td>
                    <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{l.target_name || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{detailStr(l.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SelectableTable>
        </Card>
      )}
    </div>
  );
}
